/**
 * FormDialog — a dirty-aware Dialog wrapper.
 *
 * Prevents accidental data loss on forms (outside-click, ESC, Cancel button).
 * When the form has unsaved changes (`isDirty` prop is truthy), any close
 * attempt triggers a nested "Discard unsaved changes?" AlertDialog. The user
 * picks "Continue editing" or "Discard changes". Programmatic close after a
 * successful save: consumer sets the dirty flag back to false (or just resets
 * the form state) before calling `onOpenChange(false)` — the close then
 * passes through cleanly.
 *
 * Iter 27 — usability hardening (user feedback after premium prints + snapshot
 * chain were shipped). Priority: protect operational/commercial workflows.
 *
 * Usage:
 *   <FormDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     isDirty={isDirty}
 *     contentClassName="max-w-3xl max-h-[90vh] overflow-y-auto"
 *     testId="my-form-dialog"
 *     title="Edit something"
 *     description="Short subtitle"
 *   >
 *     <form onSubmit={submit}>…</form>
 *   </FormDialog>
 */
import { createContext, useContext, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";

// Iter 37 a11y fix: every Radix Dialog MUST render a DialogTitle for screen
// readers. Some consumers pass a custom `header` (icon + complex layout)
// instead of `title`, which leaves the dialog without a Title element. This
// helper renders a visually-hidden Title so Radix's a11y guarantee holds.
const SR_HIDDEN = {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: "0",
};

/**
 * Context exposes FormDialog's `requestClose` so consumer Cancel buttons can
 * route their close through the dirty-check (otherwise a direct
 * setOpen(false) / onOpenChange(false) bypasses the discard prompt because
 * Radix's controlled `open` change does NOT round-trip through onOpenChange).
 *
 * Usage in consumer:
 *   import FormDialog, { useFormDialogClose } from ".../FormDialog";
 *   const requestClose = useFormDialogClose();
 *   <Button onClick={requestClose}>Cancel</Button>
 */
const FormDialogCloseContext = createContext(() => {});
export const useFormDialogClose = () => useContext(FormDialogCloseContext);

/**
 * Drop-in Cancel button for use inside a FormDialog. Routes its click through
 * the dialog's dirty-check so unsaved changes prompt the discard confirm.
 * Accepts the same className / data-testid / disabled / children that the
 * consumer would pass to a normal Button — defaults to outline + "Cancel".
 */
export function FormDialogCancel({
    children = "Cancel",
    className = "",
    disabled = false,
    testId,
    variant = "outline",
}) {
    const requestClose = useFormDialogClose();
    // Use a plain <button> (Tailwind-styled) instead of importing Button to
    // keep this file framework-light. Style matches the shadcn outline / ghost
    // patterns used everywhere else in the codebase.
    const variantClass = variant === "ghost"
        ? "text-slate-600 hover:bg-slate-100"
        : "border border-slate-200 bg-white hover:bg-slate-50 text-slate-900";
    return (
        <button
            type="button"
            onClick={requestClose}
            disabled={disabled}
            data-testid={testId}
            className={`inline-flex items-center justify-center rounded-md px-4 h-9 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variantClass} ${className}`}
        >
            {children}
        </button>
    );
}

export default function FormDialog({
    open,
    onOpenChange,
    isDirty = false,
    children,
    contentClassName = "",
    testId,
    title,
    description,
    header,       // optional JSX — overrides title/description if set
    srTitle,      // optional SR-only title for a11y when `header` is custom
    srDescription, // optional SR-only description for a11y when `header` is custom
    confirmTitle = "Discard unsaved changes?",
    confirmBody = "You have unsaved changes in this form. If you discard now, those changes will be lost.",
    confirmCta = "Discard changes",
    cancelCta = "Keep editing",
}) {
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Intercepts BOTH user-initiated close (click-outside, ESC) AND any
    // consumer-driven close (Cancel button, post-save). When dirty, we show
    // the confirm and require an explicit "Discard changes" click.
    const requestClose = (nextOpen) => {
        if (nextOpen) {
            onOpenChange(true);
            return;
        }
        if (isDirty) {
            setConfirmOpen(true);
            return;
        }
        onOpenChange(false);
    };

    const doDiscard = () => {
        setConfirmOpen(false);
        // Defer the parent's close by one tick so the nested AlertDialog has
        // time to unmount cleanly (prevents focus-trap thrash in Radix).
        setTimeout(() => onOpenChange(false), 10);
    };

    // onPointerDownOutside + onEscapeKeyDown — Radix fires these BEFORE the
    // open state toggles, so calling e.preventDefault() keeps the dialog open.
    const onPointerDownOutside = (e) => {
        if (isDirty) { e.preventDefault(); setConfirmOpen(true); }
    };
    const onEscapeKeyDown = (e) => {
        if (isDirty) { e.preventDefault(); setConfirmOpen(true); }
    };

    return (
        <Dialog open={open} onOpenChange={requestClose}>
            <DialogContent
                // Iter 66.7 — scroll-safety default. Many dialogs were
                // designed for desktop heights and lost their action
                // buttons on small/zoomed viewports. Applying
                // max-h-[90vh] + overflow-y-auto here guarantees the
                // Cancel/Save row stays reachable regardless of
                // content height. Consumers can still override by
                // setting their own max-h-* in contentClassName
                // (Tailwind's last-wins resolves correctly).
                className={`max-h-[90vh] overflow-y-auto ${contentClassName}`}
                data-testid={testId}
                onPointerDownOutside={onPointerDownOutside}
                onEscapeKeyDown={onEscapeKeyDown}
                onInteractOutside={onPointerDownOutside}
            >
                <FormDialogCloseContext.Provider value={() => requestClose(false)}>
                {header ? (
                    <>
                        {/* a11y: Radix requires a DialogTitle + DialogDescription.
                            When consumer passes a custom `header`, we render a
                            visually-hidden Title + Description so screen-readers
                            still announce the dialog and Radix's a11y warnings
                            stay silent. */}
                        <DialogTitle style={SR_HIDDEN}>
                            {srTitle || (typeof title === "string" ? title : "Dialog")}
                        </DialogTitle>
                        <DialogDescription style={SR_HIDDEN}>
                            {srDescription
                                || (typeof description === "string" ? description : "")
                                || (typeof srTitle === "string"
                                    ? `${srTitle} dialog`
                                    : "Dialog form")}
                        </DialogDescription>
                        {header}
                    </>
                ) : (
                    (title || description) && (
                        <DialogHeader>
                            {title && <DialogTitle>{title}</DialogTitle>}
                            {description && <DialogDescription>{description}</DialogDescription>}
                        </DialogHeader>
                    )
                )}
                {children}
                </FormDialogCloseContext.Provider>

                {/* Nested confirm — only mounted when needed */}
                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <AlertDialogContent data-testid="form-dialog-discard-confirm">
                        <AlertDialogHeader>
                            <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
                            <AlertDialogDescription>{confirmBody}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="discard-cancel-btn">{cancelCta}</AlertDialogCancel>
                            <AlertDialogAction
                                data-testid="discard-confirm-btn"
                                onClick={doDiscard}
                                className="bg-rose-600 hover:bg-rose-500 text-white"
                            >
                                {confirmCta}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DialogContent>
        </Dialog>
    );
}
